import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { CalendarClock } from "lucide-react";
import { Task2 } from "@/types/tasks";

interface TasksGanttViewProps {
  tasks: Task2[];
  onTaskSelect: (task: Task2) => void;
}

export function TasksGanttView({ tasks, onTaskSelect }: TasksGanttViewProps) {
  // Timeline: semanas ao redor da data atual
  const generateWeeks = () => {
    const weeks: Date[] = [];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - startDate.getDay());
    for (let i = -2; i < 8; i++) {
      const weekStart = new Date(startDate);
      weekStart.setDate(startDate.getDate() + i * 7);
      weeks.push(weekStart);
    }
    return weeks;
  };

  const weeks = generateWeeks();

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      not_started: "bg-gray-400",
      in_progress: "bg-blue-500",
      waiting: "bg-yellow-500",
      completed: "bg-green-500",
      cancelled: "bg-red-500",
    };
    return colors[status] || colors.not_started;
  };

  const calculateBarPosition = (startDate: Date, endDate: Date) => {
    const timelineStart = weeks[0];
    const timelineEnd = new Date(weeks[weeks.length - 1]);
    timelineEnd.setDate(timelineEnd.getDate() + 6);

    const totalDays = Math.ceil((timelineEnd.getTime() - timelineStart.getTime()) / 86400000);
    const taskStartDays = Math.ceil((startDate.getTime() - timelineStart.getTime()) / 86400000);
    const taskDuration = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / 86400000));

    const leftPercent = (taskStartDays / totalDays) * 100;
    const widthPercent = (taskDuration / totalDays) * 100;

    return {
      left: `${Math.max(0, leftPercent)}%`,
      width: `${Math.min(100 - Math.max(0, leftPercent), widthPercent)}%`,
    };
  };

  const formatWeek = (date: Date) => {
    const endWeek = new Date(date);
    endWeek.setDate(date.getDate() + 6);
    return `${date.getDate()}/${(date.getMonth() + 1).toString().padStart(2, "0")} - ${endWeek.getDate()}/${(endWeek.getMonth() + 1).toString().padStart(2, "0")}`;
  };

  if (tasks.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 flex flex-col items-center justify-center text-center text-muted-foreground gap-2">
          <CalendarClock className="h-8 w-8 opacity-40" />
          <span>Nenhuma tarefa com prazo para exibir no cronograma.</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cronograma das tarefas</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            <div className="flex border-b border-border mb-4">
              <div className="w-80 p-4 border-r border-border">
                <h4 className="font-medium">Tarefa</h4>
              </div>
              <div className="flex-1 flex">
                {weeks.map((week, index) => (
                  <div key={index} className="flex-1 p-2 text-center border-r border-border text-sm">
                    {formatWeek(week)}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {tasks.map((task) => {
                const barPosition = task.startDate && task.endDate
                  ? calculateBarPosition(task.startDate, task.endDate)
                  : null;
                const assignee = task.assignees[0];

                return (
                  <div
                    key={task.id}
                    className="flex border-b border-border/50 py-2 hover:bg-muted/50 cursor-pointer"
                    onClick={() => onTaskSelect(task)}
                  >
                    <div className="w-80 p-4 border-r border-border">
                      <div className="space-y-2">
                        <div>
                          <p className="font-medium text-sm line-clamp-1">{task.title}</p>
                          <p className="text-xs text-muted-foreground">{task.projectName}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {assignee && (
                            <>
                              <Avatar className="w-5 h-5">
                                <AvatarFallback className="text-xs">
                                  {assignee.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-xs">{assignee.name}</span>
                            </>
                          )}
                          {task.setor && (
                            <Badge variant="outline" className="text-xs">{task.setor}</Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 relative p-4">
                      {barPosition ? (
                        <div
                          className={`absolute top-1/2 -translate-y-1/2 h-6 rounded ${getStatusColor(task.status)} opacity-80`}
                          style={barPosition}
                        >
                          <div className="flex items-center justify-center h-full">
                            <span className="text-xs text-white font-medium px-2 truncate">{task.title}</span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">sem prazo definido</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
